import React, { useState } from "react";
import { Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { sendContact } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      setSent(true);
      toast({ title: "Message sent!", description: "We'll get back to you within 1–2 business days." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Couldn't send", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-[1100px] mx-auto px-6 mt-12 min-h-[60vh] fade-up">
      <h1 className="font-serif-brand text-4xl font-semibold text-[#0e2a1e]">Contact Us</h1>
      <p className="text-[#0e2a1e]/60 mt-2 mb-10 max-w-xl">
        Have a question about an order, a pick-up, or a photography session? Send us a message and we'll get back to you within 1–2 business days.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Info */}
        <div className="space-y-6">
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-[#2f6b1e] mt-0.5" />
            <div>
              <p className="font-medium text-[#0e2a1e]">Email</p>
              <a href="mailto:info@everyonesbudget.ca" className="text-[#a4820e] hover:underline text-sm">info@everyonesbudget.ca</a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin size={20} className="text-[#2f6b1e] mt-0.5" />
            <div>
              <p className="font-medium text-[#0e2a1e]">Pick-up location</p>
              <p className="text-[#0e2a1e]/70 text-sm">85 Acorn Place, Mississauga, ON · by appointment</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-[#2f6b1e] mt-0.5" />
            <div>
              <p className="font-medium text-[#0e2a1e]">Response time</p>
              <p className="text-[#0e2a1e]/70 text-sm">Within 1–2 business days</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-6">
          {sent ? (
            <div className="text-center py-8">
              <CheckCircle2 size={44} className="text-[#2f6b1e] mx-auto" />
              <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] mt-4">Thanks for reaching out!</h2>
              <p className="text-[#0e2a1e]/70 mt-2 text-sm">We've received your message and will reply soon.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-[#a4820e] font-medium text-sm hover:underline">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-[#0e2a1e]">Name</span>
                <input name="name" value={form.name} onChange={change} className="w-full mt-1.5 bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40 transition-colors" placeholder="Your name" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-[#0e2a1e]">Email</span>
                <input name="email" type="email" value={form.email} onChange={change} className="w-full mt-1.5 bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40 transition-colors" placeholder="you@example.com" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-[#0e2a1e]">Message</span>
                <textarea name="message" rows={5} value={form.message} onChange={change} className="w-full mt-1.5 bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40 transition-colors resize-none" placeholder="How can we help?" />
              </label>
              <button type="submit" disabled={loading} className="w-full bg-[#0e2a1e] text-[#f4f2ea] font-semibold py-3 rounded-full hover:bg-[#164031] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default Contact;
